'use client';

import { useState, useEffect, useCallback } from 'react';
import yaml from 'js-yaml';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, Sparkles, FileText, Upload, X, ArrowRight, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { xmlToYamlContent } from '../../data/xml-to-yaml-content';

const sampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<person>
  <name>John Doe</name>
  <age>30</age>
  <city>New York</city>
  <isActive>true</isActive>
  <skills>
    <skill>JavaScript</skill>
    <skill>TypeScript</skill>
    <skill>React</skill>
  </skills>
  <address>
    <street>123 Main St</street>
    <zip>10001</zip>
  </address>
</person>`;

const sampleYaml = `person:
  name: John Doe
  age: 30
  city: New York
  isActive: true
  skills:
    - JavaScript
    - TypeScript
    - React
  address:
    street: 123 Main St
    zip: 10001`;

function xmlToJson(xmlString: string): any {
    try {
        // Parse XML string to DOM
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

        // Check for XML parsing errors
        const parserError = xmlDoc.getElementsByTagName('parsererror');
        if (parserError.length > 0) {
            throw new Error('Invalid XML format');
        }

        // Convert XML to JavaScript object
        return xmlNodeToJson(xmlDoc.documentElement);
    } catch (error) {
        throw new Error(`Error parsing XML: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

function xmlNodeToJson(node: Element | ChildNode): any {
    // Handle text nodes
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue?.trim() || '';
        return text === '' ? null : text;
    }

    // Handle element nodes
    if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const obj: Record<string, any> = {};

        // Process attributes
        if (element.attributes.length > 0) {
            obj['@attributes'] = {};
            for (let i = 0; i < element.attributes.length; i++) {
                const attr = element.attributes[i];
                obj['@attributes'][attr.name] = attr.value;
            }
        }

        // Process child nodes
        const children = Array.from(element.childNodes);

        // If no children, return empty object or text content
        if (children.length === 0) {
            return Object.keys(obj).length > 0 ? obj : '';
        }

        // Process all child nodes
        for (const child of children) {
            if (child.nodeType === Node.TEXT_NODE) {
                const text = child.nodeValue?.trim() || '';
                if (text) {
                    obj['#text'] = text;
                }
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                const childElement = child as Element;
                const childName = childElement.tagName;

                if (!obj[childName]) {
                    obj[childName] = xmlNodeToJson(childElement);
                } else {
                    // Handle multiple elements with the same name by converting to array
                    if (!Array.isArray(obj[childName])) {
                        obj[childName] = [obj[childName]];
                    }
                    obj[childName].push(xmlNodeToJson(childElement));
                }
            }
        }

        return obj;
    }

    return null;
}

function convertXmlToYaml(xmlString: string): string {
    try {
        if (!xmlString.trim()) {
            return '';
        }

        // Convert XML to JSON object
        const jsonObj = xmlToJson(xmlString);

        // Convert JSON to YAML
        return yaml.dump(jsonObj, {
            indent: 2,
            lineWidth: -1, // No line wrapping
            skipInvalid: true,
            noRefs: true,
            sortKeys: false,
        });
    } catch (error) {
        throw new Error(`Error converting XML to YAML: ${error instanceof Error ? error.message : 'Invalid XML'}`);
    }
}

export default function XmlToYamlTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const [isConverting, setIsConverting] = useState(false);
    const { toast } = useToast();

    const convertXml = useCallback((xmlString: string) => {
        try {
            if (!xmlString.trim()) {
                setOutput('');
                setError('');
                return;
            }

            setIsConverting(true);
            const yamlString = convertXmlToYaml(xmlString);
            setOutput(yamlString);
            setError('');
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Invalid XML'}`);
            setOutput('');
        } finally {
            setIsConverting(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            convertXml(input);
        }, 500); // Slightly longer debounce for XML parsing

        return () => clearTimeout(timer);
    }, [input, convertXml]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.readAsText(file);

        // Reset the file input
        e.target.value = '';
    };

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The YAML has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleDownload = () => {
        if (!output) return;

        const blob = new Blob([output], { type: 'text/yaml;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName ? fileName.replace(/\.\w+$/, '.yaml') : 'converted_data.yaml';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
        setFileName(null);
    };

    const loadSample = () => {
        setInput(sampleXml);
        setFileName('sample.xml');
    };

    return (
        <div className="container mx-auto px-6 sm:px-8 py-12 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="XML to YAML Converter - Free Online Data Format Converter">
                Convert XML data to YAML format instantly with our free online converter. Perfect for configuration files, data serialization, and application development.
            </ToolPageIntro>
            </div>

            <div className="w-full max-w-7xl mx-auto mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="relative flex-1">
                        <label className="block text-sm font-medium text-muted-foreground mb-1">
                            Upload XML File
                        </label>
                        <label className="flex items-center justify-center w-full px-4 py-2 bg-background border border-dashed border-input rounded-md cursor-pointer hover:bg-accent/50 transition-colors">
                            <Upload className="h-4 w-4 mr-2" />
                            <span className="text-sm truncate max-w-[200px]">
                                {fileName || 'Choose a file...'}
                            </span>
                            <input
                                type="file"
                                accept=".xml,text/xml"
                                className="hidden"
                                onChange={handleFileUpload}
                                disabled={isConverting}
                            />
                        </label>
                        {fileName && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setFileName(null);
                                    setInput('');
                                }}
                                className="mt-1 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                                disabled={isConverting}
                            >
                                <X className="h-3 w-3" />
                                Remove file
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-2 mt-4 sm:mt-0">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadSample}
                            className="gap-1.5"
                            disabled={isConverting}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            Sample
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClear}
                            disabled={!input || isConverting}
                            className="text-muted-foreground hover:text-foreground"
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-6 items-stretch">
                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        XML Input
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName || 'Paste your XML here'}
                                    </CardDescription>
                                </div>
                                <div className="text-sm font-mono bg-muted/50 px-2 py-1 rounded">
                                    XML
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        'Paste your XML here...\n' +
                                        '<?xml version="1.0" encoding="UTF-8"?>\n' +
                                        '<root>\n' +
                                        '  <item>\n' +
                                        '    <name>Example</name>\n' +
                                        '    <value>123</value>\n' +
                                        '  </item>\n' +
                                        '</root>'
                                    }
                                    className="min-h-[400px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                    disabled={isConverting}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-2 flex items-center justify-center my-4 lg:my-0">
                    <div className="flex flex-col items-center">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                            <ArrowRight className="h-5 w-5" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                            Convert to
                        </p>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        YAML Output
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName ?
                                            fileName.replace(/\.\w+$/, '.yaml') :
                                            'Your YAML will appear here'}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownload}
                                        disabled={!output || isConverting}
                                        className="gap-1.5"
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                        Download
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!output || isConverting}
                                        className={cn(
                                            'gap-1.5 transition-all',
                                            isCopied
                                                ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                                : 'hover:bg-primary/5'
                                        )}
                                    >
                                        {isCopied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                        {isCopied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[400px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70',
                                        'whitespace-pre' // Preserve formatting
                                    )}
                                    placeholder="Your YAML will appear here..."
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            Your YAML will appear here
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                                {isConverting && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={xmlToYamlContent.title}
                    description={xmlToYamlContent.description}
                    content={xmlToYamlContent.content}
                    faqs={xmlToYamlContent.faqs}
                    relatedTools={xmlToYamlContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
